import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Plus, ChevronRight } from "lucide-react";
import AffiliateAgreement from "../../components/AffiliateAgreement";

const AffiliatesTab = ({ affiliates, onCreateAffiliate }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [newAffiliate, setNewAffiliate] = useState({ name: "", email: "", territory_id: "costa_del_sol" });
  const [showAgreement, setShowAgreement] = useState(false);
  const [pendingAffiliate, setPendingAffiliate] = useState(null);

  const handleCreate = async () => {
    // Show agreement before creating
    setPendingAffiliate(newAffiliate);
    setShowDialog(false);
    setShowAgreement(true);
  };

  const handleSignAgreement = async (signedPdfBlob) => {
    setShowAgreement(false);
    if (pendingAffiliate) {
      await onCreateAffiliate(pendingAffiliate, signedPdfBlob);
      setPendingAffiliate(null);
      setNewAffiliate({ name: "", email: "", territory_id: "costa_del_sol" });
    }
  };

  return (
    <div className="bg-[#0B0F19] rounded-xl border border-[#D4AF37]/10 overflow-hidden">
      <div className="p-4 border-b border-[#D4AF37]/10 flex items-center justify-between">
        <h3 className="font-['Playfair_Display'] text-xl text-white">Affiliates</h3>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="btn-gold" data-testid="add-affiliate-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Affiliate
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B0F19] border-[#D4AF37]/20 text-white">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display'] text-xl">Add New Affiliate</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-white/70">Name</Label>
                <Input
                  value={newAffiliate.name}
                  onChange={(e) => setNewAffiliate({ ...newAffiliate, name: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  data-testid="affiliate-name-input"
                />
              </div>
              <div>
                <Label className="text-white/70">Email</Label>
                <Input
                  type="email"
                  value={newAffiliate.email}
                  onChange={(e) => setNewAffiliate({ ...newAffiliate, email: e.target.value })}
                  className="bg-white/5 border-white/10 text-white mt-1"
                  data-testid="affiliate-email-input"
                />
              </div>
              <Button onClick={handleCreate} className="btn-gold w-full" data-testid="save-affiliate-btn">
                Continue to Agreement
              </Button>
              <p className="text-white/30 text-xs text-center">
                You'll be asked to review and sign the affiliate agreement before submission.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="affiliates-table">
          <thead className="bg-white/5">
            <tr>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Name</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Code</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Sales</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Revenue</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Commission</th>
              <th className="text-left p-4 text-white/50 text-sm font-medium">Dashboard</th>
            </tr>
          </thead>
          <tbody>
            {affiliates.map((a) => (
              <tr key={a.id} className="border-t border-white/5">
                <td className="p-4 text-white">{a.name}</td>
                <td className="p-4 font-mono text-[#D4AF37]">{a.affiliate_code || a.code}</td>
                <td className="p-4 text-white">{a.total_sales || 0}</td>
                <td className="p-4 text-white">€{(a.total_revenue || 0).toFixed(2)}</td>
                <td className="p-4 text-[#047857] font-bold">€{(a.total_commission || 0).toFixed(2)}</td>
                <td className="p-4">
                  <Link to={`/affiliate/${a.affiliate_code || a.code}`} target="_blank">
                    <Button variant="ghost" size="sm" className="text-[#D4AF37]">
                      View <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {affiliates.length === 0 && (
          <p className="p-8 text-center text-white/40">No affiliates yet</p>
        )}
      </div>

      {/* Affiliate Agreement Modal */}
      {showAgreement && pendingAffiliate && (
        <AffiliateAgreement
          affiliateName={pendingAffiliate.name}
          territory={pendingAffiliate.territory_id}
          onSign={handleSignAgreement}
          onCancel={() => { setShowAgreement(false); setPendingAffiliate(null); setShowDialog(true); }}
        />
      )}
    </div>
  );
};

export default AffiliatesTab;
